import { useState, useEffect } from 'react';
import { Calculator, Activity, ListChecks } from 'lucide-react';
import { BrandNav, SiteFooter, ContactCTA } from '../components/SiteChrome';
import { AckGate, useAcknowledgement } from '../components/AckGate';
import { PositionRisk } from '../components/tools/PositionRisk';
import { VolatilityRange } from '../components/tools/VolatilityRange';
import { ConditionsChecklist } from '../components/tools/ConditionsChecklist';
import { useAnalytics } from '../hooks/useAnalytics';

const TOOLS = [
  { id: 'risk', label: 'Position Risk', icon: Calculator, blurb: 'Work out position size and dollar risk from your capital, entry and stop.', Comp: PositionRisk },
  { id: 'volatility', label: 'Volatility Range', icon: Activity, blurb: 'See the typical daily, weekly and monthly price range from recent volatility.', Comp: VolatilityRange },
  { id: 'checklist', label: 'Conditions Checklist', icon: ListChecks, blurb: 'Turn the five-engine read into a checklist you can tick, extend, copy or print.', Comp: ConditionsChecklist },
];

export default function ToolsPage() {
  const [active, setActive] = useState('risk');
  const { acknowledged, acknowledge } = useAcknowledgement();
  const { track } = useAnalytics();
  const tool = TOOLS.find((t) => t.id === active);

  useEffect(() => { track('page_view', { ui_surface: 'tools_page' }); }, [track]);

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <BrandNav width="max-w-4xl" />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" data-testid="tools-title">Trade Tools</h1>
        <p className="text-sm text-zinc-500 mb-6">Free calculators built on the same data as the assessment engine.</p>

        <div className="grid grid-cols-3 gap-2 mb-6" role="tablist">
          {TOOLS.map(({ id, label, icon: Icon }) => (
            <button key={id} role="tab" aria-selected={active === id} onClick={() => setActive(id)} data-testid={`tool-tab-${id}`}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 py-3 rounded-lg border transition-colors ${active === id ? 'border-brand/60 bg-brand/10 text-white' : 'border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700'}`}>
              <Icon className={`w-4 h-4 ${active === id ? 'text-brand' : ''}`} />
              <span className="text-center leading-tight">{label}</span>
            </button>
          ))}
        </div>

        <section className="bg-zinc-950 border border-zinc-800 rounded-lg p-5 space-y-5" data-testid={`tool-panel-${active}`}>
          <p className="text-sm text-zinc-400">{tool.blurb}</p>
          <AckGate acknowledged={acknowledged} onAcknowledge={acknowledge} />
          {TOOLS.map(({ id, Comp }) => (
            <div key={id} className={id === active ? '' : 'hidden'}><Comp ready={acknowledged} /></div>
          ))}
        </section>

        <ContactCTA className="mt-8 no-print" source="tools" />
      </main>
      <SiteFooter />
    </div>
  );
}
